import { MCDLogo } from '@/assets/images';
import { useLoader, pushEvent } from '@CS/hooks';
import { connect } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Slider } from '@/components/Slider';
import { getLogged } from '@/redux/auth/selectors';
import { getCampaignId, getCampaignTranslations } from '@/redux/campaign/selectors';
import { setQuizQueue } from '@/redux/quizzes/actions';
import { getQuizQueue, getQuizzes } from '@/redux/quizzes/selectors';
import { useNavigateQuery } from '@/hooks';
import API from '@/services/api';
import type { CampaignQuiz } from '@/services/api/types';
import ShuffleOverlay from '@/components/ShuffleOverlay/ShuffleOverlay';
import {
  createQuizQueue,
  DEFAULT_MATCH_QUESTIONS,
  type GameQuestion,
  type TeamId
} from '@/utils/quizQueue';

import styles from './Game.module.css';
import type { GameProps, TeamScores } from './types';
import { Card } from '@/components/Card/Card';
import { GameHUD } from './GameHUD';

/** How many questions in a row before teams swap sides in the queue. */
const QUESTIONS_PER_ROUND = 2;

/** Max visible cards in the deck (slice of queue ending at current index). */
const MAX_STACK_CARDS = 4;

const CARD_TILT_DEG = 0.6;
const FEEDBACK_DELAY_MS = 2500;

const STACK_SHIFT_X_PX = -6;
const STACK_SHIFT_Y_PX = -10;
const STACK_SCALE_PER_LAYER = 0.02;

const GEN_ANSWER_LABELS = ['A', 'B', 'Z'] as const;

const genAnswerLabel = (index: number): string => {
  if (index < GEN_ANSWER_LABELS.length) return GEN_ANSWER_LABELS[index];
  return String.fromCharCode('C'.charCodeAt(0) + (index - GEN_ANSWER_LABELS.length));
};

const resolveWinner = (scores: TeamScores): TeamId | 'draw' => {
  if (scores.trending > scores.teletekst) return 'trending';
  if (scores.teletekst > scores.trending) return 'teletekst';
  return 'draw';
};

const buildGameResultPath = (scores: TeamScores): string => {
  const winner = resolveWinner(scores);
  const { trending, teletekst } = scores;
  return `/game-result?winner=${winner}&trending=${trending}&teletekst=${teletekst}`;
};

const formatRoundLabel = (roundWord: string, currentRound: number, totalRounds: number): string =>
  `${roundWord} ${currentRound}/${totalRounds}`;

const teamCssColor = (team: TeamId): string =>
  team === 'trending' ? 'var(--teamTrending)' : 'var(--teamTeletekst)';

const stackLayerTransform = (depth: number, tiltDeg: number): string => `
  translateX(${depth * STACK_SHIFT_X_PX}px)
  translateY(${depth * STACK_SHIFT_Y_PX}px)
  scale(${1 - depth * STACK_SCALE_PER_LAYER})
  rotate(${tiltDeg}deg)
`;

const INITIAL_SCORES: TeamScores = {
  teletekst: 0,
  trending: 0
};

const Game = ({ quizzes, quizQueue, campaignId, logged, setQuizQueue, translations }: GameProps) => {
  const navigate = useNavigateQuery();

  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<'shuffle' | 'playing'>('shuffle');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [scores, setScores] = useState<TeamScores>(INITIAL_SCORES);
  const [submitting, setSubmitting] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [answerFeedback, setAnswerFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  const feedbackTimeoutRef = useRef<number | null>(null);
  const scoreAnimationTimeoutRef = useRef<number | null>(null);
  const renderedCardsRef = useRef<Set<string>>(new Set());
  const quizQueueLengthRef = useRef<number>(0);
  const scoresRef = useRef<TeamScores>(INITIAL_SCORES);

  const loader = useLoader(loading);

  const currentQuestion = quizQueue[questionIndex];
  
  const questionText = currentQuestion?.text ?? '';
  const activeTeam = currentQuestion?.team ?? 'teletekst';
  const tableOrientationDeg: 0 | 180 = activeTeam === 'teletekst' ? 0 : 180;

  const totalRounds = Math.max(1, Math.ceil(quizQueue.length / QUESTIONS_PER_ROUND));
  const currentRound = Math.min(
    totalRounds,
    Math.floor(questionIndex / QUESTIONS_PER_ROUND) + 1
  );
  const roundLabel = formatRoundLabel(
    translations.questionround,
    currentRound,
    totalRounds
  );

  const stackStartIndex = Math.max(0, questionIndex - (MAX_STACK_CARDS - 1));
  const stackCards = quizQueue.slice(stackStartIndex, questionIndex + 1);

  useEffect(() => {
    if (!currentQuestion) return;
    pushEvent({
      event: 'page_view',
      eventProperties: {
        page_virtual_path: `/fcg26/ronde_${currentRound}/${currentQuestion.text.toLowerCase().replace(/\s+/g, '_')}`,
        page_type: '/fam_card_game26',
        team: activeTeam
      }
    });
  }, [questionIndex]);

  const questionClassName = (() => {
    const len = questionText.trim().length;
    if (len > 35) return `${styles.question} ${styles.questionSm}`;
    return styles.question;
  })();

  useEffect(() => {
    const applyQueue = (sourceQuizzes: CampaignQuiz[]) => {
      setQuizQueue(createQuizQueue(sourceQuizzes, DEFAULT_MATCH_QUESTIONS));
    };

    if (quizQueue?.length) {
      setLoading(false);
      return;
    }

    if (quizzes?.length) {
      applyQueue(quizzes);
      setLoading(false);
      return;
    }

    if (!logged || !campaignId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadQuizQueue = async () => {
      setLoading(true);
      try {
        const sourceQuizzes = (await API.getCampaignQuizzes()).quizzes;
        if (!cancelled) applyQueue(sourceQuizzes);
      } catch (error) {
        if (error instanceof Error && error.message === 'Already fetching quizzes.') return;
        console.error('Failed to load quiz queue:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadQuizQueue();

    return () => {
      cancelled = true;
    };
  }, [logged, campaignId, quizzes, quizQueue.length, setQuizQueue]);

  useEffect(() => {
    setQuestionIndex(0);
    setScores(INITIAL_SCORES);
    setSelectedOptionId(null);
    setAnswerFeedback(null);
    setPhase('shuffle');
    renderedCardsRef.current.clear();
  }, [quizQueue]);

  useEffect(
    () => () => {
      if (feedbackTimeoutRef.current) window.clearTimeout(feedbackTimeoutRef.current);
      if (scoreAnimationTimeoutRef.current) window.clearTimeout(scoreAnimationTimeoutRef.current);
    },
    []
  );

  useEffect(() => {
    quizQueueLengthRef.current = quizQueue.length;
  }, [quizQueue.length]);

  useEffect(() => {
    scoresRef.current = scores;
  }, [scores]);

  const scheduleAfterFeedback = useCallback(
    (finalScores: TeamScores) => {
      if (feedbackTimeoutRef.current) window.clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = window.setTimeout(() => {
        setQuestionIndex(prev => {
          const next = prev + 1;
          if (next < quizQueueLengthRef.current) {
            setSelectedOptionId(null);
            setAnswerFeedback(null);
            return next;
          }
          navigate(buildGameResultPath(finalScores), { remove: true });
          return prev;
        });
      }, FEEDBACK_DELAY_MS);
    },
    [navigate]
  );

  const handleOptionClick = useCallback(
    async (optionId: string) => {
      if (!currentQuestion || submitting || answerFeedback || selectedOptionId) return;

      setSubmitting(true);
      setSelectedOptionId(optionId);

      try {
        const result = await API.answerCampaignQuiz(currentQuestion.id, optionId);
        const nextScores = result.correct
          ? { ...scoresRef.current, [activeTeam]: scoresRef.current[activeTeam] + 1 }
          : scoresRef.current;

        setAnswerFeedback(result.correct ? 'correct' : 'incorrect');

        pushEvent({
          event: 'page_view',
          eventProperties: {
            page_virtual_path: `/fcg26/ronde_${currentRound}/question_${currentQuestion.text.toLowerCase().replace(/\s+/g, '_')}/${result.correct ? 'answer_true' : 'answer_false'}`,
            page_type: '/fam_card_game26',
            team: activeTeam
          }
        });

        if (scoreAnimationTimeoutRef.current) window.clearTimeout(scoreAnimationTimeoutRef.current);
        scoreAnimationTimeoutRef.current = window.setTimeout(() => {
          if (result.correct) setScores(nextScores);
        }, 800);

        scheduleAfterFeedback(nextScores);
      } catch {
        setAnswerFeedback('incorrect');
        scheduleAfterFeedback(scoresRef.current);
      } finally {
        setSubmitting(false);
      }
    },
    [
      activeTeam,
      currentQuestion,
      answerFeedback,
      selectedOptionId,
      submitting,
      scheduleAfterFeedback,
    ]
  );

  const closeMenu = () => setIsMenuOpen(false);
  const openMenu = () => setIsMenuOpen(true);
  const openRules = () => setIsRulesOpen(true);
  const closeRules = () => setIsRulesOpen(false);

  if (loading) return loader;

  return (
    <div className={styles.outer}>
      {phase === 'shuffle' && quizQueue.length > 0 && (
        <ShuffleOverlay
          quizQueue={quizQueue}
          onDone={() => setPhase('playing')}
        />
      )}

      {phase === 'playing' && (
        <>
          <GameHUD
            scores={scores}
            questionIndex={questionIndex}
            tableOrientationDeg={tableOrientationDeg}
            isMenuOpen={isMenuOpen}
            isRulesOpen={isRulesOpen}
            translations={translations}
            onOpenMenu={openMenu}
            onCloseMenu={closeMenu}
            onOpenRules={openRules}
            onCloseRules={closeRules}
          />

          <div className={styles.deck} aria-label="Question deck">
            {stackCards.map((q, i) => {
              const depth = stackCards.length - 1 - i;
              const isTop = depth === 0;
              const cardTeam = q.team;
              const cardRotation: 0 | 180 = cardTeam === 'teletekst' ? 0 : 180;
              const tiltDeg = cardTeam === 'trending' ? CARD_TILT_DEG : -CARD_TILT_DEG;
              const enterSide = cardTeam === 'teletekst' ? 'left' : 'right';
              
              const isFirstRender = isTop && !renderedCardsRef.current.has(q.id);
              if (isFirstRender) {
                renderedCardsRef.current.add(q.id);
              }
              
              return (
                <Card
                  key={q.id}
                  isTop={isTop}
                  zIndex={i}
                  enterSide={enterSide}
                  shouldAnimate={isFirstRender}
                  isInverted={cardTeam === 'trending'}
                  stackTransform={stackLayerTransform(depth, tiltDeg)}
                  innerStyle={{
                    backgroundColor: teamCssColor(cardTeam),
                    transform: `rotate(${cardRotation}deg)`
                  }}
                >
                  {isTop && (
                    <>
                      <div className={styles.cardHeader}>
                        <span>Gen {cardTeam}</span>
                        <img src={MCDLogo} alt="McDonald's Logo" className={styles.mcdLogo} />
                        <span>{roundLabel}</span>
                      </div>

                      <div className={styles.questionContainer}>
                        <h4 className={questionClassName}>{q.text || ''}</h4>
                      </div>

                      <div className={styles.options}>
                        {q.options?.map((option: GameQuestion['options'][number], optionIndex: number) => {
                          const sliderSuccess =
                            answerFeedback && selectedOptionId === option.id
                              ? answerFeedback === 'correct'
                              : undefined;
                          
                          const isDisabled =
                            selectedOptionId !== null && selectedOptionId !== option.id;

                          return (
                            <div
                              key={option.id}
                              className={
                                isDisabled
                                  ? `${styles.answerSlider} ${styles.answerSliderDisabled}`
                                  : styles.answerSlider
                              }
                            >
                              <Slider 
                                text={option.text}
                                label={genAnswerLabel(optionIndex)}
                                isSuccess={sliderSuccess}
                                onReady={() => handleOptionClick(option.id)}
                                translations={translations}
                                inverted={cardTeam === 'trending'}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: never) => ({
  campaignId: getCampaignId(state),
  quizzes: getQuizzes(state),
  quizQueue: getQuizQueue(state),
  logged: getLogged(state),
  translations: getCampaignTranslations(state)
});

const mapDispatchToProps = {
  setQuizQueue
};

export default connect(mapStateToProps, mapDispatchToProps)(Game);